import { httpsCallable } from 'firebase/functions';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { functions } from '@/config';

import { TableEmptyStateArt } from '../TableEmptyState';
import {
  ButtonsContainer,
  DangerWrapper,
  Description,
  ErrorCodeBadge,
  ErrorContent,
  ErrorDetails,
  ErrorDetailsContainer,
  ErrorSummary,
  GlobalErrorContainer,
  IconWrapper,
  PrimaryButton,
  ReportButton,
  ReportStatus,
  SecondaryButton,
  Title,
} from './GlobalError.styled';

interface GlobalErrorProps {
  error?: Error | null;
  errorInfo?: React.ErrorInfo | null;
  onRetry?: () => void;
  onGoHome?: () => void;
}

export const GlobalError: FC<GlobalErrorProps> = ({
  error,
  errorInfo,
  onRetry,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const isDevelopment = import.meta.env.DEV;

  const [reportStatus, setReportStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleReport = async () => {
    setReportStatus('sending');

    try {
      const reportError = httpsCallable(functions, 'v3-reportError');

      await reportError({
        errorMessage: error?.message || 'Unknown error',
        errorStack: error?.stack || null,
        componentStack: errorInfo?.componentStack || null,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });

      setReportStatus('success');
    } catch {
      setReportStatus('error');
    }
  };

  return (
    <GlobalErrorContainer>
      <DangerWrapper>
        <IconWrapper>
          <TableEmptyStateArt variant="error" height={140} />
        </IconWrapper>

        {error?.name && (
        <ErrorCodeBadge>
          {t('globalError.title')}
        </ErrorCodeBadge>
        )}

        <Title>{error?.message || t('globalError.defaultMessage')}</Title>

        {/* Ślad stosu to materiał dla nas, nie dla użytkownika — schowany pod
            drobnym linkiem, żeby ekran błędu nie straszył ścianą tekstu.
            Nadal w zasięgu jednego kliknięcia, gdy trzeba go skopiować. */}
        {isDevelopment && (error?.stack || errorInfo?.componentStack) && (
          <ErrorDetailsContainer>
            {error?.stack && (
              <ErrorDetails>
                <ErrorSummary>{t('globalError.showDetails', 'Szczegóły techniczne')}</ErrorSummary>
                <ErrorContent>{error.stack}</ErrorContent>
              </ErrorDetails>
            )}

            {errorInfo?.componentStack && (
              <ErrorDetails>
                <ErrorSummary>{t('globalError.showComponentStack', 'Ślad komponentów')}</ErrorSummary>
                <ErrorContent>{errorInfo.componentStack}</ErrorContent>
              </ErrorDetails>
            )}
          </ErrorDetailsContainer>
        )}

        <ButtonsContainer>
          <PrimaryButton onClick={onRetry || (() => window.location.reload())}>
            {onRetry ? t('globalError.retry') : t('globalError.refresh')}
          </PrimaryButton>
          {onGoHome && (
            <SecondaryButton onClick={onGoHome}>{t('globalError.goHome')}</SecondaryButton>
          )}
          <ReportButton
            onClick={handleReport}
            disabled={reportStatus === 'sending' || reportStatus === 'success'}
          >
            {reportStatus === 'sending' && t('globalError.reportSending')}
            {reportStatus === 'success' && t('globalError.reportSent')}
            {(reportStatus === 'idle' || reportStatus === 'error') && t('globalError.report')}
          </ReportButton>
        </ButtonsContainer>

        {reportStatus === 'success' && (
          <ReportStatus $variant="success">{t('globalError.reportSuccess')}</ReportStatus>
        )}
        {reportStatus === 'error' && (
          <ReportStatus $variant="error">{t('globalError.reportError')}</ReportStatus>
        )}
      </DangerWrapper>
    </GlobalErrorContainer>
  );
};
